(() => {
  const ROOT_SELECTOR = '[data-aimi-motion-root]';
  const MEDIA_SELECTOR = '[data-aimi-motion-media]';
  const ENTER_SELECTOR = '[data-aimi-motion-enter]';
  const REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const ENTER_STAGGER_MS = 50;
  const SCROLL_SCRUB = 0.18;
  const DESKTOP_SCROLL_LIMIT = 18;
  const MOBILE_SCROLL_LIMIT = 10;
  const DESKTOP_SETTLE_LIMIT = 14;
  const MOBILE_SETTLE_LIMIT = 8;

  /** @type {WeakSet<Element>} */
  const initializedRoots = new WeakSet();
  /** @type {WeakSet<Element>} */
  const revealedMedia = new WeakSet();
  /** @type {Set<HTMLElement>} */
  const activeMedia = new Set();
  /** @type {WeakMap<HTMLElement, { current: number, previousRaw: number, stableFrames: number }>} */
  const mediaState = new WeakMap();
  /** @type {number | null} */
  let frameId = null;

  if (REDUCED_MOTION_QUERY.matches || isLowPowerDevice()) return;

  document.documentElement.classList.add('aimi-luxury-motion');

  const mediaObserver = new IntersectionObserver(handleMediaIntersection, {
    rootMargin: '8% 0px 8% 0px',
    threshold: [0, 0.01, 0.2, 0.6, 1],
  });

  const revealObserver = new IntersectionObserver(handleMediaReveal, {
    rootMargin: '0px 0px -6% 0px',
    threshold: 0.08,
  });

  REDUCED_MOTION_QUERY.addEventListener('change', (event) => {
    if (event.matches) disableMotion();
  });

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  /**
   * @param {Element} root
   */
  function initRoot(root) {
    if (!(root instanceof HTMLElement)) return;
    if (initializedRoots.has(root)) return;
    if (root.closest(ROOT_SELECTOR) !== root) return;

    initializedRoots.add(root);
    annotateEnterSequence(root);
    registerMedia(root);
    playEnterSequence(root);
  }

  /**
   * @param {HTMLElement} root
   */
  function annotateEnterSequence(root) {
    const sequence = [
      findFirst(root, '[data-aimi-motion-gallery-shell], [data-aimi-motion-featured-media], [data-aimi-motion-gallery]'),
      findFirst(root, 'product-title, .product-title, [data-block-type="product-title"]'),
      findFirst(root, '.price, price-list, product-price, [data-block-type="price"]'),
      findFirst(root, 'variant-picker, variant-selects, .variant-picker, .product-form__input'),
      findFirst(root, 'buy-buttons, .buy-buttons, product-form, product-form-component, add-to-cart-component, button[name="add"]'),
    ];

    uniqueElements(sequence).forEach((element, index) => {
      element.setAttribute('data-aimi-motion-enter', '');
      element.style.setProperty('--aimi-enter-delay', `${index * ENTER_STAGGER_MS}ms`);
      element.style.setProperty('--aimi-enter-duration', index === 0 ? '1120ms' : '940ms');
    });
  }

  /**
   * @param {HTMLElement} root
   */
  function playEnterSequence(root) {
    const enterItems = Array.from(root.querySelectorAll(ENTER_SELECTOR)).filter(
      (item) => item instanceof HTMLElement
    );

    if (!enterItems.length) return;

    root.classList.add('is-aimi-motion-entering');

    let pending = enterItems.length;
    const finish = (item) => {
      item.style.removeProperty('will-change');
      pending -= 1;

      if (pending <= 0) {
        root.classList.remove('is-aimi-motion-entering');
        root.classList.add('is-aimi-motion-entered');
      }
    };

    enterItems.forEach((item) => {
      item.addEventListener(
        'animationend',
        () => {
          finish(item);
        },
        { once: true }
      );
    });
  }

  /**
   * @param {ParentNode} scope
   */
  function registerMedia(scope) {
    const mediaItems =
      scope instanceof Element && scope.matches(MEDIA_SELECTOR)
        ? [scope, ...scope.querySelectorAll(MEDIA_SELECTOR)]
        : Array.from(scope.querySelectorAll(MEDIA_SELECTOR));

    mediaItems.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;
      if (item.dataset.aimiMotionRegistered === 'true') return;

      item.dataset.aimiMotionRegistered = 'true';
      item.dataset.aimiMotionPendingReveal = 'true';
      mediaObserver.observe(item);
      revealObserver.observe(item);
    });
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  function handleMediaIntersection(entries) {
    entries.forEach((entry) => {
      if (!(entry.target instanceof HTMLElement)) return;

      // MOBILE FIX: The scroll-linked parallax loop (tick) forces a layout read
      // every animation frame for as long as a product image is on screen, which
      // saturates the main thread and makes taps feel like they need 3-4 presses
      // on phones. Desktop keeps the full effect; mobile never activates the loop.
      // Images still fade in via the separate (cheap, one-shot) reveal observer.
      if (MOBILE_QUERY.matches) return;

      if (entry.isIntersecting && !entry.target.hidden) {
        activeMedia.add(entry.target);
        entry.target.classList.add('is-aimi-motion-active');
        startTicker();
      } else {
        deactivateMedia(entry.target);
      }
    });
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  function handleMediaReveal(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || !(entry.target instanceof HTMLElement)) return;
      if (revealedMedia.has(entry.target)) return;

      revealedMedia.add(entry.target);
      entry.target.classList.add('is-aimi-media-revealed');
      delete entry.target.dataset.aimiMotionPendingReveal;
      revealObserver.unobserve(entry.target);
    });
  }

  function startTicker() {
    if (frameId !== null) return;

    frameId = window.requestAnimationFrame(tick);
  }

  function disableMotion() {
    document.documentElement.classList.remove('aimi-luxury-motion');

    if (frameId !== null) {
      window.cancelAnimationFrame(frameId);
      frameId = null;
    }

    mediaObserver.disconnect();
    revealObserver.disconnect();
    activeMedia.forEach(deactivateMedia);
    activeMedia.clear();

    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      root.classList.remove('is-aimi-motion-entering', 'is-aimi-motion-entered');
    });

    document.querySelectorAll(`${ENTER_SELECTOR}, ${MEDIA_SELECTOR}`).forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      item.classList.remove('is-aimi-motion-active', 'is-aimi-motion-settling', 'is-aimi-media-revealed');
      item.style.removeProperty('--aimi-scroll-y');
      item.style.removeProperty('--aimi-enter-delay');
      item.style.removeProperty('--aimi-enter-duration');
      item.style.removeProperty('will-change');
      delete item.dataset.aimiMotionPendingReveal;
    });
  }

  function tick() {
    frameId = null;

    if (!activeMedia.size) return;

    // MOBILE FIX: Defensive bail-out in case the viewport crossed the breakpoint
    // (e.g. orientation change) while media was active. Keeps the per-frame layout
    // work off the main thread on phones.
    if (MOBILE_QUERY.matches) {
      activeMedia.forEach(deactivateMedia);
      activeMedia.clear();
      return;
    }

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const scrollLimit = MOBILE_QUERY.matches ? MOBILE_SCROLL_LIMIT : DESKTOP_SCROLL_LIMIT;
    const settleLimit = MOBILE_QUERY.matches ? MOBILE_SETTLE_LIMIT : DESKTOP_SETTLE_LIMIT;
    const writes = [];

    activeMedia.forEach((item) => {
      if (!item.isConnected) {
        cleanupMedia(item);
        return;
      }

      if (item.hidden) {
        deactivateMedia(item);
        return;
      }

      const rect = item.getBoundingClientRect();

      if (rect.bottom <= 0 || rect.top >= viewportHeight) return;

      const progress = clamp((viewportHeight - rect.top) / (viewportHeight + rect.height), 0, 1);
      const rawTarget = -scrollLimit * progress;
      const state = getMediaState(item);
      const rawDelta = Math.abs(rawTarget - state.previousRaw);

      state.stableFrames = rawDelta < 0.035 ? state.stableFrames + 1 : 0;
      state.previousRaw = rawTarget;

      const shouldSettle = rawTarget <= -(scrollLimit - 0.8) && state.stableFrames > 5;
      const target = shouldSettle ? -settleLimit : rawTarget;
      state.current += (target - state.current) * SCROLL_SCRUB;

      if (Math.abs(target - state.current) < 0.01) {
        state.current = target;
      }

      writes.push([item, state.current, shouldSettle]);
    });

    writes.forEach(([item, current, shouldSettle]) => {
      item.style.setProperty('--aimi-scroll-y', `${current.toFixed(3)}px`);
      item.classList.toggle('is-aimi-motion-settling', Boolean(shouldSettle));
    });

    if (activeMedia.size) startTicker();
  }

  /**
   * @param {HTMLElement} item
   */
  function deactivateMedia(item) {
    activeMedia.delete(item);
    item.classList.remove('is-aimi-motion-active', 'is-aimi-motion-settling');
    item.style.removeProperty('will-change');
  }

  /**
   * @param {HTMLElement} item
   */
  function cleanupMedia(item) {
    deactivateMedia(item);
    mediaObserver.unobserve(item);
    revealObserver.unobserve(item);
  }

  /**
   * @param {HTMLElement} item
   * @returns {{ current: number, previousRaw: number, stableFrames: number }}
   */
  function getMediaState(item) {
    const existing = mediaState.get(item);
    if (existing) return existing;

    const state = { current: 0, previousRaw: 0, stableFrames: 0 };
    mediaState.set(item, state);
    return state;
  }

  /**
   * @param {HTMLElement} root
   * @param {string} selector
   * @returns {HTMLElement | null}
   */
  function findFirst(root, selector) {
    const element = root.querySelector(selector);
    return element instanceof HTMLElement ? element : null;
  }

  /**
   * @param {(HTMLElement | null)[]} elements
   * @returns {HTMLElement[]}
   */
  function uniqueElements(elements) {
    return elements.filter((element, index, list) => element && list.indexOf(element) === index);
  }

  /**
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function isLowPowerDevice() {
    return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
  }

  document.addEventListener('aimi:media-gallery-updated', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const root = target?.closest(ROOT_SELECTOR);

    if (root) {
      registerMedia(root);

      // MOBILE FIX: This listener fires on EVERY variant/color tap. On desktop it
      // re-activates the parallax loop; on mobile we skip the forced getBoundingClientRect
      // reads entirely so the tap repaints instantly instead of stuttering.
      if (MOBILE_QUERY.matches) return;

      root.querySelectorAll(MEDIA_SELECTOR).forEach((item) => {
        if (!(item instanceof HTMLElement) || item.hidden) return;

        const rect = item.getBoundingClientRect();
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

        if (rect.bottom > 0 && rect.top < viewportHeight) {
          activeMedia.add(item);
          item.classList.add('is-aimi-motion-active');
          startTicker();
        }
      });
    }
  });

  ['shopify:section:load', 'section:load'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.target instanceof Element) {
        if (event.target.matches(ROOT_SELECTOR)) initRoot(event.target);
        event.target.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
      }
    });
  });

  ['shopify:section:unload', 'section:unload'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (!(event.target instanceof Element)) return;

      if (event.target.matches(MEDIA_SELECTOR) && event.target instanceof HTMLElement) cleanupMedia(event.target);
      event.target.querySelectorAll(MEDIA_SELECTOR).forEach((item) => {
        if (item instanceof HTMLElement) cleanupMedia(item);
      });
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();